/**
 * CashCraft configuration and shared types.
 */

/** The operator (admin) pubkey. Orders are encrypted and addressed to this key. */
export const ADMIN_PUBKEY =
  "df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc";

/** Custom event kind for encrypted website build orders (see NIP.md). */
export const ORDER_KIND = 4011;

/**
 * Addressable event kind for operator-published order status updates.
 * The `d` tag is the order event id, so the latest status per order wins.
 */
export const ORDER_STATUS_KIND = 38273;

/** Ordered lifecycle of an order. */
export const ORDER_STATUSES = [
  "new",
  "deposit_paid",
  "building",
  "delivered",
  "paid_in_full",
  "cancelled",
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_META: Record<
  OrderStatus,
  { label: string; className: string }
> = {
  new: { label: "New", className: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  deposit_paid: { label: "Deposit paid", className: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  building: { label: "Building", className: "bg-purple-500/15 text-purple-400 border-purple-500/30" },
  delivered: { label: "Delivered", className: "bg-teal-500/15 text-teal-400 border-teal-500/30" },
  paid_in_full: { label: "Paid in full", className: "bg-primary/15 text-primary border-primary/30" },
  cancelled: { label: "Cancelled", className: "bg-destructive/15 text-destructive border-destructive/30" },
};

/**
 * Fixed relays that orders are always published to AND read from. Using an
 * explicit group (instead of the user's personal relay pool) guarantees the
 * operator's /orders inbox reads from the same place customers wrote to,
 * regardless of whichever NIP-65 relays the operator happens to be using.
 */
export const ORDER_RELAYS = [
  "wss://relay.ditto.pub/",
  "wss://relay.primal.net/",
  "wss://nos.lol/",
  "wss://relay.dreamith.to/",
];

/** The operator's Bitcoin Cash receiving address (where customers pay). */
export const BCH_ADDRESS =
  "bitcoincash:qrfe5r4afrvfyzcldap2fcvetusqz2u0ky8h0y9292";

/**
 * PayPal "buyer enters amount" checkout link for paying in USD by card/PayPal.
 * The customer types the amount themselves, so the UI must clearly display the
 * exact amount they should enter.
 */
export const PAYPAL_LINK = "https://www.paypal.com/ncp/payment/NFJAN48AQWHUQ";

/** Numeric USD price for each tier, used for live BCH conversion. */
export const TIER_USD: Record<string, number> = {
  starter: 99,
  growth: 249,
  commerce: 599,
};

/** Fraction of the tier price required as an upfront deposit to begin work. */
export const DEPOSIT_RATE = 0.5;

/** Included revision rounds per tier. */
export const TIER_REVISIONS: Record<string, number> = {
  starter: 1,
  growth: 2,
  commerce: 3,
};

/** Flat fee (USD) for each extra revision round beyond what's included. */
export const EXTRA_REVISION_FEE_USD = 40;

/** Number of days after delivery during which included revisions apply. */
export const REVISION_WINDOW_DAYS = 7;

/** The decrypted shape of an order payload. */
export interface OrderPayload {
  project: string;
  siteType: string;
  budget: string;
  pages: string;
  style: string;
  color: string;
  features: string[];
  contact: string;
  bchAddress: string;
  details: string;
}

export interface SiteTypeOption {
  value: string;
  label: string;
  description: string;
}

export const SITE_TYPES: SiteTypeOption[] = [
  { value: "landing", label: "Landing Page", description: "One-page site to promote a product or idea" },
  { value: "store", label: "Online Store", description: "Sell products & accept Bitcoin Cash" },
  { value: "blog", label: "Blog / Content", description: "Publish articles and updates" },
  { value: "portfolio", label: "Portfolio", description: "Showcase your work or brand" },
  { value: "custom", label: "Something Custom", description: "Tell us what you have in mind" },
];

export const STYLE_OPTIONS = [
  { value: "modern", label: "Modern" },
  { value: "minimal", label: "Minimal" },
  { value: "bold", label: "Bold" },
  { value: "playful", label: "Playful" },
];

export const FEATURE_OPTIONS = [
  { value: "bch-payments", label: "Accept Bitcoin Cash" },
  { value: "booking", label: "Bookings / Scheduling" },
  { value: "blog", label: "Blog" },
  { value: "contact-form", label: "Contact Form" },
  { value: "gallery", label: "Photo Gallery" },
  { value: "newsletter", label: "Newsletter Signup" },
  { value: "multilingual", label: "Multiple Languages" },
  { value: "animations", label: "Fancy Animations" },
];

export interface PricingTier {
  id: string;
  name: string;
  priceUsd: string;
  tagline: string;
  features: string[];
  highlighted?: boolean;
}

/**
 * A portfolio project to show prospective clients.
 *
 * TO ADD A NEW PROJECT:
 *  1. Put a screenshot in `public/portfolio/` (e.g. public/portfolio/acme.jpg).
 *     Landscape images around 1200x800 look best.
 *  2. Add an entry to the PORTFOLIO array below.
 *  3. That's it — it appears in the grid automatically.
 */
export interface PortfolioItem {
  /** Project / business name. */
  title: string;
  /** One or two sentences about what you built. */
  description: string;
  /** Path to the screenshot, e.g. "/portfolio/acme.jpg". */
  image: string;
  /** Short category tags, e.g. ["Online Store", "BCH payments"]. */
  tags: string[];
  /** Optional live site URL — shows a "Visit site" link when present. */
  url?: string;
}

export const PORTFOLIO: PortfolioItem[] = [
  {
    title: "BCH Nostr",
    description:
      "A decentralized social network where people own their identity, content, and money. The community hub that ties the whole ecosystem together.",
    image: "/portfolio/bchnostr.png",
    tags: ["Web App", "Social", "Nostr"],
    url: "https://bchnostr.com",
  },
  {
    title: "BCHPay.cash",
    description:
      "A fast, mobile-first point-of-sale web app that lets any merchant accept payments in seconds. Clean checkout UX with instant confirmation.",
    image: "/portfolio/bchpay.png",
    tags: ["Web App", "Payments", "Point of Sale"],
    url: "https://bchpay.cash",
  },
  {
    title: "BCH Local",
    description:
      "A sleek merchant directory that helps people discover businesses near them, with live stats and an interactive map. Polished dark UI, built on the Nostr network.",
    image: "/portfolio/bchlocal.png",
    tags: ["Web App", "Directory", "Maps"],
    url: "https://bchlocal.com",
  },
  {
    title: "BCH School",
    description:
      "An interactive learning platform with lessons, XP, badges, and certificates. Engaging, beginner-friendly design that makes a complex topic simple.",
    image: "/portfolio/bchschool.png",
    tags: ["Web App", "Education", "Gamified"],
    url: "https://bchschool.com",
  },
  // TO ADD A REAL PROJECT: drop a screenshot in public/portfolio/ and add an
  // entry here. Example:
  // {
  //   title: "Bean & Brew Roastery",
  //   description: "A single-page store with online ordering.",
  //   image: "/portfolio/bean-and-brew.jpg",
  //   tags: ["Online Store", "Payments"],
  //   url: "https://example.com",
  // },
];

export interface HostingPlan {
  id: string;
  name: string;
  priceUsd: number;
  tagline: string;
  features: string[];
  highlighted?: boolean;
}

export const HOSTING_PLANS: HostingPlan[] = [
  {
    id: "basic",
    name: "Basic",
    priceUsd: 9.99,
    tagline: "Keep a simple site online, fast and secure.",
    features: [
      "Reliable, fast hosting",
      "Free SSL / HTTPS",
      "Uptime monitoring",
      "Monthly backups",
      "Email support",
    ],
  },
  {
    id: "standard",
    name: "Standard",
    priceUsd: 15.99,
    tagline: "Hosting plus a helping hand with updates.",
    features: [
      "Everything in Basic",
      "Weekly backups",
      "1 content update / month",
      "Domain connection help",
      "Faster support response",
    ],
    highlighted: true,
  },
  {
    id: "premium",
    name: "Premium",
    priceUsd: 39,
    tagline: "For stores & active sites that need attention.",
    features: [
      "Everything in Standard",
      "Daily backups",
      "Up to 4 content updates / month",
      "Priority support",
      "Performance & security checks",
    ],
  },
];

export const PRICING_TIERS: PricingTier[] = [
  {
    id: "starter",
    name: "Starter",
    priceUsd: "$99",
    tagline: "A sharp one-page site to get you online fast.",
    features: [
      "Single-page site",
      "Mobile-friendly design",
      "Contact / call-to-action",
      "Accept Bitcoin Cash",
      "1 revision round included",
      "Delivered in ~48 hours",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    priceUsd: "$249",
    tagline: "A multi-page site for a growing business.",
    features: [
      "Up to 5 pages",
      "Custom design & branding",
      "BCH payments built in",
      "Blog or gallery",
      "Contact form",
      "2 revision rounds included",
      "Delivered in ~4 days",
    ],
    highlighted: true,
  },
  {
    id: "commerce",
    name: "Commerce",
    priceUsd: "$599+",
    tagline: "A full store or custom build, paid in cash.",
    features: [
      "Online store or custom app",
      "Unlimited pages",
      "Bitcoin Cash checkout",
      "Advanced features & integrations",
      "Priority support",
      "3 revision rounds included",
      "Custom timeline",
    ],
  },
];
